import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { sendMessage, fetchTopicSummary, clearAllFooterButtonsHistory, addMessage, setSurveyEnabled } from '@/store/slices/chatSlice';
import type { StreamingSegment } from '@/store/slices/chatSlice';
import { BubbleX, Markdown, Button } from '@@components';
import { HistoryTopic } from '@@service/api';
import { trackEvent } from '@/service/tracking';
import Survey from '@/views/survey';
import PaymentModal from '../payment-modal';
import ToolCallsBlock from '../tool-calls/index';
import HistoryTopicList from '../history-topic-list';
import { userSelectedResolved } from '@/service/api';
import './index.less';

export interface BotItemProps {
    content: string;
    isStreaming?: boolean;
    className?: string;
    messageId?: string;
    type?: 'text' | 'image' | 'pdf' | 'html' | 'survey' | 'history-topics';
    topics?: HistoryTopic[];
}

// 内联工具块：渲染单个 segment 的工具调用（无框化）
const InlineToolBlock: React.FC<{
    toolActivities: StreamingSegment['toolActivities'];
    isProcessing?: boolean;
}> = ({ toolActivities, isProcessing = false }) => {
    if (!toolActivities || toolActivities.order.length === 0) return null;

    const toolCount = toolActivities.order.length;
    const isCompleted = !!toolActivities.completed && !isProcessing;

    const [ expanded, setExpanded ] = React.useState(!isCompleted);

    React.useEffect(() => {
        if (isCompleted) setExpanded(false);
    }, [ isCompleted ]);

    React.useEffect(() => {
        if (isProcessing) setExpanded(true);
    }, [ isProcessing ]);

    return (
        <ToolCallsBlock
            inlineActivities={toolActivities}
            inlineProcessing={isProcessing}
            toolCount={toolCount}
        />
    );
};

const BotItem: React.FC<BotItemProps> = ({
    content,
    isStreaming = false,
    className = '',
    messageId,
    type,
    topics
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const {
        streamingFooterButtons,
        footerButtonsHistory,
        status,
        isOpen,
        streamingSegments,
        segmentsHistory,
        currentSessionId
    } = useSelector((state: RootState) => state.chat);

    const [ iframeOpen, setIframeOpen ] = useState(false);
    const [ iframeUrl, setIframeUrl ] = useState<string>('');

    const footerButtons = useMemo(() => {
        if (messageId && footerButtonsHistory && footerButtonsHistory[messageId]) {
            return footerButtonsHistory[messageId];
        }
        return streamingFooterButtons;
    }, [ messageId, footerButtonsHistory, streamingFooterButtons ]);

    // 获取当前渲染的 segments
    const segments: StreamingSegment[] = useMemo(() => {
        if (messageId && segmentsHistory[messageId]) {
            return segmentsHistory[messageId];
        }
        if (!messageId) {
            return streamingSegments;
        }
        return [];
    }, [ messageId, segmentsHistory, streamingSegments ]);

    const hasSegments = segments.length > 0;

    const handleButtonClick = (btn: any) => {
        const { event } = btn || {};
        if (!event) return;
        dispatch(clearAllFooterButtonsHistory());

        const eventName = event.action === 'resolved' ? 'ResolvedActionClick' : 'ContinueActionClick';
        trackEvent({
            eventName,
            eventData: {}
        });

        if (event.type === 'quick_reply' && event.reply_text) {
            dispatch(sendMessage({ content: event.reply_text }));
        } else if (event.type === 'url' && event.url) {
            setIframeUrl(event.url);
            setIframeOpen(true);
        } else if (event.type === 'timeout_action') {
            if (event.action === 'resolved') {
                dispatch(setSurveyEnabled(true));
                dispatch(addMessage({
                    id: `timeout-survey-${Date.now()}`,
                    content: 'Glad to hear your issue is resolved! Could you spare a moment to fill out this quick survey?',
                    role: 'assistant',
                    timestamp: Date.now(),
                    type: 'survey'
                }));
                userSelectedResolved().catch((error) => {
                    console.log('userSelectedResolved invoke error:', error);
                });
            } else if (event.action === 'more_help') {
                const replyText = event.reply_text || btn?.label || 'Need more help';
                dispatch(sendMessage({ content: replyText }));
            }
        }
    };

    const handlePaymentReport = (billable: { status: boolean; data?: any }) => {
        dispatch(sendMessage({ billable }));
    };

    const handleTopicClick = (topicId: string, topicText: string) => {
        dispatch(fetchTopicSummary({ topicId, topicText }));
    };

    // 渲染消息体：支持 segments 交替排列
    const renderMessageBody = () => {
        if (type === 'history-topics' && topics && topics.length > 0) {
            return <HistoryTopicList topics={topics} onTopicClick={handleTopicClick} />;
        }

        // 有 segments 时按顺序渲染工具块和内容块
        if (hasSegments) {
            return (
                <>
                    {segments.map((seg, idx) => {
                        if (seg.type === 'tool') {
                            const isLastSegment = idx === segments.length - 1;
                            const isProcessing = isLastSegment && !messageId && status === 'streaming';
                            return (
                                <InlineToolBlock
                                    key={`tool-${idx}`}
                                    toolActivities={seg.toolActivities}
                                    isProcessing={isProcessing}
                                />
                            );
                        }
                        if (seg.type === 'content' && seg.content) {
                            return <Markdown key={`content-${idx}`} content={seg.content} />;
                        }
                        return null;
                    })}
                    {(isStreaming || (!messageId && !content && status === 'streaming')) && (
                        <div className="streaming-indicator">
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                        </div>
                    )}
                </>
            );
        }

        // 兼容：没有 segments 时用传统方式渲染（纯文本消息、无工具调用）
        return (
            <>
                {/* 兼容旧的 ToolCallsBlock（无 segments 的历史消息） */}
                <ToolCallsBlock messageId={messageId} />
                <Markdown content={content} />
                {(isStreaming || (!messageId && !content && status === 'streaming')) && (
                    <div className="streaming-indicator">
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={`bot-item ${className} ${isStreaming ? 'streaming' : ''}`}>
            <BubbleX
                variant="borderless"
                content={content}
                messageRender={() => (
                    <div className="bot-message">
                        {renderMessageBody()}
                    </div>
                )}
                avatar={{
                    icon: <img src={require('@/assets/bot-white.png')} alt="Robot" />
                }}
                footer={(
                    type === 'survey'
                        ? (
                            <div className="bot-footer-survey">
                                <Survey />
                            </div>
                        )
                        : (footerButtons?.length ? (
                            <div className="bot-footer-actions">
                                {footerButtons.map((btn: any, idx: number) => (
                                    <Button
                                        key={idx}
                                        size="small"
                                        type="primary"
                                        onClick={() => handleButtonClick(btn)}
                                        disabled={!isOpen}
                                    >
                                        {btn?.label}
                                    </Button>
                                ))}
                            </div>
                        ) : (<></>))
                )}
                placement="start"
                className="bot-bubble"
            />

            <PaymentModal
                open={iframeOpen}
                url={iframeUrl}
                title="This Device Is Out Of Warranty"
                sessionId={currentSessionId ?? undefined}
                onClose={() => {
                    setIframeOpen(false);
                    setIframeUrl('');
                }}
                onReport={handlePaymentReport}
            />
        </div>
    );
};

export default BotItem;
